import { Link, useNavigate } from "react-router-dom";
import { Wordmark } from "./Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, UserCircle, LayoutDashboard, Wallet } from "lucide-react";
import { homeFor } from "./ProtectedRoute";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/?tab=live", label: "In-Play" },
  { to: "/?tab=upcoming", label: "Upcoming" },
  { to: "/?tab=completed", label: "Results" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-amber-500/15"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link">
          <Wordmark />
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          {navItems.map((it) => (
            <Link
              key={it.label}
              to={it.to}
              className="font-display uppercase tracking-widest text-sm text-zinc-300 hover:text-amber-400 transition-colors"
              data-testid={`nav-${it.label.toLowerCase()}`}
            >
              {it.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {user && user.role !== "admin" && (
            <div className="hidden sm:flex items-center gap-1.5 border border-amber-500/30 rounded-md px-3 h-9 text-sm" data-testid="header-credits">
              <Wallet className="h-4 w-4 text-amber-400" />
              <span className="font-mono-stat text-amber-300">{(user.credits || 0).toFixed(2)}</span>
            </div>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-amber-500/40 text-amber-300 hover:text-black hover:bg-amber-400"
                  data-testid="account-menu-trigger"
                >
                  <UserCircle className="mr-2 h-4 w-4" /> {user.display_name || user.email}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-950 border-amber-500/20">
                <DropdownMenuLabel className="text-amber-400 font-display uppercase tracking-wider">
                  {{ admin: "Administrator", master: "Master", agent: "Agent", client: "Client" }[user.role]}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate(homeFor(user.role))}
                  data-testid="menu-dashboard"
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  data-testid="menu-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={() => navigate("/login")}
              className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black font-display uppercase tracking-widest hover:brightness-110"
              data-testid="login-cta-btn"
            >
              Login
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
